import {classToPlain, Exclude, Expose, plainToClass, Type} from "class-transformer";
import {Entity} from "./entity";
import * as t from "io-ts";
import {v4 as uuid} from "uuid";
import {validateJSON} from "../web/validation";
import {ObjectPreferenceArrayCodec, ObjectPreferenceEntity} from "./object-preference";
import {WaterGoPreferenceEntity, WaterGoPreferenceCodec} from "./water-go-preference";
import {OilGoPreferenceEntity, OilGoPreferenceCodec} from "./oil-go-preference";

/** Codec for a region type. */
export const RegionCodec = t.intersection([
  t.type({
    id: t.string,
    name: t.string,
    areaUnitOfMeasure: t.string,
    volumnUnitOfMeasure: t.string,
    weightUnitOfMeasure: t.string,
    timeZone: t.string,
    dispatchRequestDuration: t.number,
    dispatchMatchTime: t.number,
    dispatchUserIsAdmin: t.boolean,
  }),
  t.partial({
    geographicName: t.string,
    linearMeasure: t.string,
    pressureUom: t.string,
    odometerUom: t.string,
    temperatureUom: t.string,
    meterOutUom: t.string,
    regulatedByRrc: t.boolean,
    lockDownDttm: t.number,
    createDttm: t.number,
    lastModifiedDttm: t.number,
    objectPreferences: ObjectPreferenceArrayCodec,
    waterGoPreferences: WaterGoPreferenceCodec,
    oilGoPreference: OilGoPreferenceCodec,
  }),
]);

/** Codec for an array of regions. */
export const RegionArrayCodec = t.array(RegionCodec);

export type Region = t.TypeOf<typeof RegionCodec>;
export type RegionArray = t.TypeOf<typeof RegionArrayCodec>;

/**
 * The unit of measure options for a region.
 */
export type UnitOfMeasureOptions = {
  areaUnitOfMeasure: string;
  volumeUnitOfMeasure: string;
  weightUnitOfMeasure: string;
  linearUnitOfMeasure?: string;

};

/**
 * The dispatch options for a region.
 */
export type DispatchOptions = {
  defaultDispatchRequestDuration: number;
  dispatchMatchTime: number;
  dispatchUserIsAdmin: boolean;
};

/**
 * The default dispatch options for a region.
 */
export const defaultDispatchOptions: DispatchOptions = {
  defaultDispatchRequestDuration: 36,
  dispatchMatchTime: 720,
  dispatchUserIsAdmin: false,
};

/**
 * A region.
 */
@Exclude()
export class RegionEntity implements Entity {
  @Expose()
  id!: string;

  @Expose()
  name!: string;

  @Expose()
  geographicName?: string;

  @Expose()
  timeZone!: string;

  @Expose()
  dispatchRequestDuration!: number;

  @Expose()
  dispatchMatchTime!: number;

  @Expose()
  dispatchUserIsAdmin!: boolean;

  @Expose()
  areaUnitOfMeasure!: string;

  @Expose({name: 'volumnUnitOfMeasure'})
  volumeUnitOfMeasure!: string;

  @Expose()
  weightUnitOfMeasure!: string;

  @Expose({name: 'linearMeasure'})
  linearUnitOfMeasure?: string;

  @Expose({name: 'pressureUom'})
  pressureUnitOfMeasure?: string

  @Expose({name: 'odometerUom'})
  odometerUnitOfMeasure?: string

  @Expose({name: 'temperatureUom'})
  temperatureUnitOfMeasure?: string

  @Expose({name: 'meterOutUom'})
  meterUnitOfMeasure?: string

  @Expose()
  regulatedByRrc?: boolean;

  @Expose()
  lockDownDttm?: number

  @Expose()
  createDttm?: number;

  @Expose()
  lastModifiedDttm?: number;

  @Expose()
  @Type(() => ObjectPreferenceEntity)
  objectPreferences?: ObjectPreferenceEntity[]

  @Expose()
  @Type(() => WaterGoPreferenceEntity)
  waterGoPreference?: WaterGoPreferenceEntity

  @Expose()
  @Type(() => OilGoPreferenceEntity)
  oilGoPreferenceEntity?: OilGoPreferenceEntity

  readonly entityType = 'REGION';

  get displayName(): Readonly<string> {
    return this.name;
  }

  /**
   * Constructor for RegionEntity. Recommend using the {@link create} method instead.
   * @hideconstructor
   */
  constructor() {
    // Empty constructor for 'class-transformer'
  }

  /**
   * Create a RegionEntity.
   *
   * @param firstName The region's first name.
   * @param lastName The region's last name.
   * @param type The type of region.
   * @param companyId The primary key of the region's employer.
   * @param id (optional) The primary key of the region.
   */
  static create(
    name: string,
    timeZone: string,
    uomOptions: UnitOfMeasureOptions,
    dispatchOptions: DispatchOptions = defaultDispatchOptions,
    id: string | undefined = uuid()
  ): RegionEntity {
    const entity = new RegionEntity();
    entity.id = id;
    entity.name = name;
    entity.timeZone = timeZone;
    entity.areaUnitOfMeasure = uomOptions.areaUnitOfMeasure;
    entity.volumeUnitOfMeasure = uomOptions.volumeUnitOfMeasure;
    entity.weightUnitOfMeasure = uomOptions.weightUnitOfMeasure;
    entity.linearUnitOfMeasure = uomOptions.linearUnitOfMeasure;
    entity.dispatchRequestDuration =
      dispatchOptions.defaultDispatchRequestDuration;
    entity.dispatchMatchTime = dispatchOptions.dispatchMatchTime;
    entity.dispatchUserIsAdmin = dispatchOptions.dispatchUserIsAdmin;
    return entity;
  }

  /**
   * Create a RegionEntity from a Region type value.
   *
   * @param region The region type value.
   */
  static from(region: Region): RegionEntity {
    const entity: RegionEntity = plainToClass(RegionEntity, region, {
      excludeExtraneousValues: true,
    });
    return entity;
  }

  /**
   * Converts the RegionEntity to it's JSON type equivalent.
   *
   * @returns A promise that returns a Region type if fullfiled, otherwise is rejected with validation errors.
   */
  toJSONValue(): Promise<Region> {
    const plainValue = classToPlain<RegionEntity>(this, {
      excludeExtraneousValues: true,
    });

    return validateJSON(RegionCodec, plainValue);
  }

  get createDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.createDttm) {
      return new Date(this.createDttm);
    } else {
      return undefined;
    }
  }

  set createDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.createDttm = date.getTime();
    } else {
      this.createDttm = undefined;
    }
  }

  get lastModifiedDate(): Date | undefined {
    /* istanbul ignore next */
    if (this.lastModifiedDttm) {
      return new Date(this.lastModifiedDttm);
    } else {
      return undefined;
    }
  }

  set lastModifiedDate(date: Date | undefined) {
    /* istanbul ignore next */
    if (date) {
      this.lastModifiedDttm = date.getTime();
    } else {
      this.lastModifiedDttm = undefined;
    }
  }
}

/** Permission keys that control which regions a user can work in. */
export const regionPermissions = {
  /** Switch the active region between the regions assigned to the user. */
  switch: "region_switch",
  /** Access every region, including the Global (no region) view. */
  globalAccess: "region_global_access",
} as const;
